import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  Shield,
  Eye,
  Play,
  Pause,
  RotateCcw,
  AlertCircle,
  Car,
  Zap,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stateConfig = {
  open: { label: 'Open', color: 'bg-green-100 text-green-800', icon: ChevronUp },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-800', icon: ChevronDown },
  locked_open: { label: 'Locked Open', color: 'bg-yellow-100 text-yellow-800', icon: Lock },
  locked_closed: { label: 'Locked Closed', color: 'bg-red-100 text-red-800', icon: Lock },
  unknown: { label: 'Unknown', color: 'bg-purple-100 text-purple-800', icon: AlertCircle },
  error: { label: 'Error', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const commandStatusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  success: { label: 'Success', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
  timeout: { label: 'Timeout', color: 'bg-orange-100 text-orange-800' },
  retrying: { label: 'Retrying', color: 'bg-purple-100 text-purple-800' },
};

function formatDateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function AdminBarrierControl() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [lanes, setLanes] = useState([]);
  const [commands, setCommands] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('control');
  
  // Command modal state
  const [showCommandModal, setShowCommandModal] = useState(false);
  const [selectedLane, setSelectedLane] = useState(null);
  const [commandType, setCommandType] = useState('');
  const [commandReason, setCommandReason] = useState('');
  const [commandLoading, setCommandLoading] = useState(false);
  
  // Override modal state (Feature 4)
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideAction, setOverrideAction] = useState('');
  const [overrideDuration, setOverrideDuration] = useState('15');
  const [overrideReason, setOverrideReason] = useState('');
  
  // Command detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState(null);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sites`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data.sites || data || []);
        if ((data.sites || data).length > 0 && !selectedSite) {
          setSelectedSite((data.sites || data)[0].site_id);
        }
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchLanes = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const response = await fetch(`${API_URL}/api/barrier/lanes?site_id=${selectedSite}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setLanes(data.lanes || []);
      }
    } catch (error) {
      console.error('Error fetching lanes:', error);
    }
  }, [selectedSite]);

  const fetchCommands = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (selectedSite) params.append('site_id', selectedSite);
      
      const response = await fetch(`${API_URL}/api/barrier/commands?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCommands(data.commands || []);
      }
    } catch (error) {
      console.error('Error fetching commands:', error);
    }
  }, [selectedSite]);

  const fetchStats = useCallback(async () => {
    try {
      const params = selectedSite ? `?site_id=${selectedSite}` : '';
      const response = await fetch(`${API_URL}/api/barrier/stats${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [selectedSite]);

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    if (selectedSite) {
      setLoading(true);
      Promise.all([fetchLanes(), fetchCommands(), fetchStats()])
        .finally(() => setLoading(false));
    }
  }, [selectedSite, fetchLanes, fetchCommands, fetchStats]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedSite) {
        fetchLanes();
        fetchStats();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedSite, fetchLanes, fetchStats]);

  const sendCommand = async (lane, command, reason) => {
    setCommandLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/barrier/command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          site_id: selectedSite,
          lane_id: lane.lane_id,
          command: command,
          reason: reason || `Manual ${command} from admin`,
          force: false,
        }),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        if (result.status === 'success') {
          toast.success(`Barrier ${command} command successful (MOCKED)`);
        } else {
          toast.error(`Barrier command failed: ${result.message}`);
        }
        fetchLanes();
        fetchCommands();
        fetchStats();
      } else {
        toast.error(result.detail || 'Command failed');
      }
    } catch (error) {
      toast.error('Failed to send command');
    } finally {
      setCommandLoading(false);
      setShowCommandModal(false);
      setCommandReason('');
    }
  };

  const handleOverride = async () => {
    if (!selectedLane || !overrideAction) return;
    
    setCommandLoading(true);
    try {
      // Map override action to barrier command
      const commandMap = {
        open_once: 'open',
        close_now: 'close',
        hold_open: 'lock_open',
        lock_closed: 'lock_closed',
        return_auto: 'unlock',
      };
      
      const response = await fetch(`${API_URL}/api/barrier/command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          site_id: selectedSite,
          lane_id: selectedLane.lane_id,
          command: commandMap[overrideAction],
          reason: overrideReason || `Manual override: ${overrideAction}`,
          force: true,
        }),
      });
      
      const result = await response.json();
      
      if (response.ok && result.status === 'success') {
        toast.success(`Override ${overrideAction} successful (MOCKED)`);
        
        // Update override state in DB for hold_open and lock_closed
        if (overrideAction === 'hold_open' || overrideAction === 'lock_closed') {
          // The backend handles this via the barrier_states collection
        }
        
        fetchLanes();
        fetchCommands();
      } else {
        toast.error(result.detail || result.message || 'Override failed');
      }
    } catch (error) {
      toast.error('Failed to execute override');
    } finally {
      setCommandLoading(false);
      setShowOverrideModal(false);
      setOverrideReason('');
      setOverrideAction('');
    }
  };

  const viewCommandDetails = async (cmd) => {
    try {
      const response = await fetch(`${API_URL}/api/barrier/commands/${cmd.command_id}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedCommand(data);
        setShowDetailModal(true);
      }
    } catch (error) {
      setSelectedCommand(cmd);
      setShowDetailModal(true);
    }
  };

  const StateBadge = ({ state }) => {
    const config = stateConfig[state] || stateConfig.unknown;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`} data-testid={`state-badge-${state}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const StatusBadge = ({ status }) => {
    const config = commandStatusConfig[status] || commandStatusConfig.pending;
    return (
      <Badge className={config.color} data-testid={`status-badge-${status}`}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-barrier-control-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Barrier Control</h1>
          <p className="text-gray-500">Manage barrier operations and commands</p>
          <Badge variant="outline" className="mt-1 bg-yellow-50 text-yellow-700 border-yellow-300">
            <AlertTriangle className="h-3 w-3 mr-1" />
            MOCKED - No hardware connected
          </Badge>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-[200px]" data-testid="site-selector">
              <SelectValue placeholder="Select Site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((site) => (
                <SelectItem key={site.site_id} value={site.site_id}>
                  {site.name || site.site_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => { fetchLanes(); fetchCommands(); fetchStats(); }}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => setActiveTab('history')}
            data-testid="stats-total"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Commands Today</p>
                  <p className="text-2xl font-bold">{stats.today?.total || 0}</p>
                </div>
                <Zap className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:border-green-400 transition-colors"
            onClick={() => setActiveTab('history')}
            data-testid="stats-success"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Successful</p>
                  <p className="text-2xl font-bold text-green-600">{stats.today?.success || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:border-red-400 transition-colors"
            onClick={() => setActiveTab('history')}
            data-testid="stats-failed"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{stats.today?.failed || 0}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:border-purple-400 transition-colors"
            onClick={() => setActiveTab('failover')}
            data-testid="stats-rate"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Success Rate</p>
                  <p className="text-2xl font-bold">{stats.today?.success_rate || 0}%</p>
                </div>
                <Activity className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="control" data-testid="tab-control">
            <Shield className="h-4 w-4 mr-2" />
            Barrier Control
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="h-4 w-4 mr-2" />
            Command History
          </TabsTrigger>
          <TabsTrigger value="failover" data-testid="tab-failover">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Failover
          </TabsTrigger>
        </TabsList>

        {/* Control Tab */}
        <TabsContent value="control" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Barrier Lanes</CardTitle>
              <CardDescription>Current state and controls for each barrier lane</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : lanes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No barrier lanes configured for this site</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {lanes.map((lane) => (
                    <div
                      key={lane.lane_id}
                      className="border rounded-lg p-4 flex items-center justify-between"
                      data-testid={`lane-card-${lane.lane_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${lane.lane_type === 'entry' ? 'bg-blue-100' : 'bg-green-100'}`}>
                          {lane.lane_type === 'entry' ? (
                            <ChevronDown className="h-6 w-6 text-blue-600" />
                          ) : (
                            <ChevronUp className="h-6 w-6 text-green-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">{lane.lane_id}</p>
                          <p className="text-sm text-gray-500 capitalize">{lane.lane_type || 'Unknown'} Lane</p>
                        </div>
                        <StateBadge state={lane.current_state} />
                        {lane.override_active && (
                          <Badge variant="destructive" className="flex items-center gap-1">
                            <Lock className="h-3 w-3" />
                            Override Active
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Quick Actions */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedLane(lane);
                            setCommandType('open');
                            setShowCommandModal(true);
                          }}
                          data-testid={`open-btn-${lane.lane_id}`}
                        >
                          <ChevronUp className="h-4 w-4 mr-1" />
                          Open
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedLane(lane);
                            setCommandType('close');
                            setShowCommandModal(true);
                          }}
                          data-testid={`close-btn-${lane.lane_id}`}
                        >
                          <ChevronDown className="h-4 w-4 mr-1" />
                          Close
                        </Button>
                        
                        {/* Override Menu */}
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setSelectedLane(lane);
                            setShowOverrideModal(true);
                          }}
                          data-testid={`override-btn-${lane.lane_id}`}
                        >
                          <Shield className="h-4 w-4 mr-1" />
                          Override
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Command History</CardTitle>
              <CardDescription>Recent barrier commands and their status</CardDescription>
            </CardHeader>
            <CardContent>
              {commands.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No commands recorded yet</p>
                </div>
              ) : (
                <Table data-testid="commands-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Lane</TableHead>
                      <TableHead>Command</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Attempts</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commands.map((cmd) => (
                      <TableRow key={cmd.command_id} data-testid={`cmd-row-${cmd.command_id}`}>
                        <TableCell className="text-sm">
                          {formatDateTime(cmd.created_at)}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {cmd.lane_id}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {cmd.command}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={cmd.status} />
                        </TableCell>
                        <TableCell>
                          {cmd.total_attempts || 1}
                        </TableCell>
                        <TableCell className="text-sm">
                          {cmd.total_duration_ms}ms
                        </TableCell>
                        <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">
                          {cmd.reason}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewCommandDetails(cmd)}
                            data-testid={`view-cmd-${cmd.command_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Failover Tab - Feature 5 */}
        <TabsContent value="failover" className="mt-4">
          <div className="space-y-4">
            {/* Failover State Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Failover Configuration
                </CardTitle>
                <CardDescription>
                  Configure barrier behavior when failures occur
                </CardDescription>
              </CardHeader>
              <CardContent>
                {lanes.length === 0 ? (
                  <p className="text-gray-500">No barrier lanes configured</p>
                ) : (
                  <div className="space-y-4">
                    {lanes.map((lane) => (
                      <div
                        key={lane.lane_id}
                        className="border rounded-lg p-4"
                        data-testid={`failover-lane-${lane.lane_id}`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${
                              lane.failover_state === 'failover_active' ? 'bg-red-100' :
                              lane.failover_state === 'degraded' ? 'bg-yellow-100' :
                              'bg-green-100'
                            }`}>
                              <AlertTriangle className={`h-5 w-5 ${
                                lane.failover_state === 'failover_active' ? 'text-red-600' :
                                lane.failover_state === 'degraded' ? 'text-yellow-600' :
                                'text-green-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-semibold">{lane.lane_id}</p>
                              <p className="text-sm text-gray-500">
                                Failover State: {' '}
                                <Badge variant={
                                  lane.failover_state === 'failover_active' ? 'destructive' :
                                  lane.failover_state === 'degraded' ? 'warning' :
                                  'secondary'
                                }>
                                  {lane.failover_state || 'normal'}
                                </Badge>
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Select 
                              defaultValue={lane.failover_mode || 'fail_secure'}
                              onValueChange={async (value) => {
                                try {
                                  await fetch(`${API_URL}/api/barrier/failover/config`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                    },
                                    credentials: 'include',
                                    body: JSON.stringify({
                                      site_id: selectedSite,
                                      lane_id: lane.lane_id,
                                      failover_mode: value,
                                      max_consecutive_failures: 3,
                                      failure_window_minutes: 5,
                                      auto_recovery_enabled: true,
                                    }),
                                  });
                                  toast.success(`Failover mode set to ${value}`);
                                  fetchLanes();
                                } catch (e) {
                                  toast.error('Failed to update failover config');
                                }
                              }}
                            >
                              <SelectTrigger className="w-[180px]" data-testid={`failover-mode-${lane.lane_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="fail_safe">
                                  <span className="flex items-center gap-2">
                                    <Unlock className="h-4 w-4 text-green-600" />
                                    Fail-Safe (Open)
                                  </span>
                                </SelectItem>
                                <SelectItem value="fail_secure">
                                  <span className="flex items-center gap-2">
                                    <Lock className="h-4 w-4 text-red-600" />
                                    Fail-Secure (Closed)
                                  </span>
                                </SelectItem>
                                <SelectItem value="manual">
                                  <span className="flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-yellow-600" />
                                    Manual Override
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {(lane.failover_state === 'failover_active' || lane.failover_state === 'degraded') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await fetch(`${API_URL}/api/barrier/failover/clear/${selectedSite}/${lane.lane_id}`, {
                                      method: 'POST',
                                      credentials: 'include',
                                    });
                                    toast.success('Failover state cleared');
                                    fetchLanes();
                                  } catch (e) {
                                    toast.error('Failed to clear failover');
                                  }
                                }}
                                data-testid={`clear-failover-${lane.lane_id}`}
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Clear Failover
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Recent Failures</p>
                            <p className="font-semibold">{lane.recent_failure_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Last Failure</p>
                            <p className="font-semibold">
                              {lane.last_failure_at ? formatDateTime(lane.last_failure_at) : 'None'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Failure Type</p>
                            <p className="font-semibold">{lane.last_failure_type || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Failover Behavior Documentation */}
            <Card>
              <CardHeader>
                <CardTitle>Failover Behavior</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4 bg-green-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Unlock className="h-5 w-5 text-green-600" />
                      <h4 className="font-semibold text-green-800">Fail-Safe</h4>
                    </div>
                    <p className="text-sm text-green-700">
                      Barrier opens on failure. Vehicles can exit freely when system fails.
                      Use when allowing exit is more important than security.
                    </p>
                  </div>
                  
                  <div className="border rounded-lg p-4 bg-red-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="h-5 w-5 text-red-600" />
                      <h4 className="font-semibold text-red-800">Fail-Secure</h4>
                    </div>
                    <p className="text-sm text-red-700">
                      Barrier stays closed on failure. Requires manual intervention.
                      Use when security is more important than convenience.
                    </p>
                  </div>
                  
                  <div className="border rounded-lg p-4 bg-yellow-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="h-5 w-5 text-yellow-600" />
                      <h4 className="font-semibold text-yellow-800">Manual</h4>
                    </div>
                    <p className="text-sm text-yellow-700">
                      Alert operator for manual decision. No automatic action taken.
                      Use when each failure needs individual assessment.
                    </p>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Failure Scenarios Handled</h4>
                  <ul className="text-sm space-y-1 text-gray-600">
                    <li>• <strong>Relay Command Failed:</strong> Hardware communication timeout or error</li>
                    <li>• <strong>ANPR Failure:</strong> Camera or recognition system unavailable</li>
                    <li>• <strong>Payment Success, Barrier Failed:</strong> Customer paid but barrier won't open</li>
                    <li>• <strong>Duplicate Exit Read:</strong> Same vehicle detected multiple times</li>
                    <li>• <strong>Internet Unavailable:</strong> Connection to cloud services lost</li>
                    <li>• <strong>Backend Unavailable:</strong> API server not responding</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Command Confirmation Modal */}
      <Dialog open={showCommandModal} onOpenChange={setShowCommandModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {commandType === 'open' ? (
                <ChevronUp className="h-5 w-5 text-green-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-600" />
              )}
              Confirm Barrier {commandType === 'open' ? 'Open' : 'Close'}
            </DialogTitle>
            <DialogDescription>
              Send {commandType} command to barrier {selectedLane?.lane_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span><strong>MOCKED:</strong> No actual hardware will be controlled</span>
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Reason (Optional)
              </label>
              <Input
                value={commandReason}
                onChange={(e) => setCommandReason(e.target.value)}
                placeholder="Enter reason for command..."
                data-testid="command-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommandModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => sendCommand(selectedLane, commandType, commandReason)}
              disabled={commandLoading}
              className={commandType === 'open' ? 'bg-green-600 hover:bg-green-700' : ''}
              data-testid="confirm-command-btn"
            >
              {commandLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : commandType === 'open' ? (
                <ChevronUp className="h-4 w-4 mr-2" />
              ) : (
                <ChevronDown className="h-4 w-4 mr-2" />
              )}
              {commandType === 'open' ? 'Open Barrier' : 'Close Barrier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override Modal (Feature 4) */}
      <Dialog open={showOverrideModal} onOpenChange={setShowOverrideModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Shield className="h-5 w-5" />
              Manual Override
            </DialogTitle>
            <DialogDescription>
              Override automatic barrier operation for {selectedLane?.lane_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Override commands bypass normal operation. Use with caution.</span>
              </p>
              <p className="text-xs text-red-600 mt-1">MOCKED: No actual hardware will be controlled</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Override Action
              </label>
              <div className="grid grid-cols-1 gap-2">
                <Button
                  variant={overrideAction === 'open_once' ? 'default' : 'outline'}
                  className="justify-start"
                  onClick={() => setOverrideAction('open_once')}
                  data-testid="override-open-once"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Open Once - Single open then return to auto
                </Button>
                <Button
                  variant={overrideAction === 'close_now' ? 'default' : 'outline'}
                  className="justify-start"
                  onClick={() => setOverrideAction('close_now')}
                  data-testid="override-close-now"
                >
                  <Pause className="h-4 w-4 mr-2" />
                  Close Now - Immediate close
                </Button>
                <Button
                  variant={overrideAction === 'hold_open' ? 'default' : 'outline'}
                  className="justify-start"
                  onClick={() => setOverrideAction('hold_open')}
                  data-testid="override-hold-open"
                >
                  <Lock className="h-4 w-4 mr-2 text-yellow-600" />
                  Hold Open - Lock barrier in open position
                </Button>
                <Button
                  variant={overrideAction === 'lock_closed' ? 'default' : 'outline'}
                  className="justify-start"
                  onClick={() => setOverrideAction('lock_closed')}
                  data-testid="override-lock-closed"
                >
                  <Lock className="h-4 w-4 mr-2 text-red-600" />
                  Lock Closed - Lock barrier in closed position
                </Button>
                <Button
                  variant={overrideAction === 'return_auto' ? 'default' : 'outline'}
                  className="justify-start"
                  onClick={() => setOverrideAction('return_auto')}
                  data-testid="override-return-auto"
                >
                  <RotateCcw className="h-4 w-4 mr-2 text-green-600" />
                  Return to Auto - Clear override, resume normal operation
                </Button>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Reason *
              </label>
              <Input
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Enter reason for override..."
                data-testid="override-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowOverrideModal(false);
              setOverrideAction('');
              setOverrideReason('');
            }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleOverride}
              disabled={commandLoading || !overrideAction || !overrideReason}
              data-testid="confirm-override-btn"
            >
              {commandLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Shield className="h-4 w-4 mr-2" />
              )}
              Execute Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Command Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Command Details</DialogTitle>
            <DialogDescription>
              {selectedCommand?.command_id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCommand && (
            <div className="space-y-4" data-testid="command-detail-content">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Command</p>
                  <Badge variant="outline" className="capitalize text-lg">
                    {selectedCommand.command}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <StatusBadge status={selectedCommand.status} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Lane</p>
                  <p className="font-mono">{selectedCommand.lane_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Final State</p>
                  <StateBadge state={selectedCommand.final_barrier_state} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Attempts</p>
                  <p>{selectedCommand.total_attempts || 1}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p>{selectedCommand.total_duration_ms}ms</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Reason</p>
                  <p>{selectedCommand.reason}</p>
                </div>
                {selectedCommand.vehicle_registration && (
                  <div>
                    <p className="text-sm text-gray-500">Vehicle</p>
                    <p className="font-mono">{selectedCommand.vehicle_registration}</p>
                  </div>
                )}
              </div>

              {/* Raw Request */}
              <div>
                <h4 className="font-semibold mb-2">Raw Request</h4>
                <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto border">
                  {JSON.stringify(selectedCommand.raw_request, null, 2)}
                </pre>
              </div>

              {/* Raw Response */}
              <div>
                <h4 className="font-semibold mb-2">Raw Response</h4>
                <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto border">
                  {JSON.stringify(selectedCommand.raw_response, null, 2)}
                </pre>
              </div>

              {/* Attempt History */}
              {selectedCommand.attempts && selectedCommand.attempts.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Attempt History</h4>
                  <div className="space-y-2">
                    {selectedCommand.attempts.map((attempt, idx) => (
                      <div key={idx} className="text-sm border rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium">Attempt #{attempt.attempt_number}</span>
                          <StatusBadge status={attempt.status} />
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                          <span>Started: {formatDateTime(attempt.started_at)}</span>
                          <span>Duration: {attempt.duration_ms}ms</span>
                        </div>
                        {attempt.error && (
                          <p className="text-xs text-red-600 mt-1">{attempt.error}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-700">
                  <strong>is_mocked: true</strong> - This command was simulated. No actual hardware was controlled.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
